import React from 'react';
import { FormattedMessage, FormattedDate, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Row, Col } from 'antd';
import { UpOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { media, Span, Flex } from 'ui';
import { withEllipsis } from 'ui/styles';
import Spinner from 'common/spinner/components/spinner';

const Bar = styled(Row)`
  background-color: ${p => p.theme.colors.orderStatusbar};
  box-shadow: 0px 5px 20px 3px gray;
  color: ${p => p.theme.colors.text};
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  margin-left: 0 !important;
  margin-right: 0 !important;
  height: auto;
  ${media.desktop`
    height: 100px !important;
  `}
  ${media.tablet`height: 120px !important;`};
  border-top: 1px solid ${p => p.theme.colors.border};
  z-index: 1;

  > div:last-child {
    border: none;
  }
`;

const Column = styled(Col)`
  white-space: nowrap;
  ${media.tablet`
    padding-top: 5px;
    padding-bottom: 5px;`}
  height: 100%;
`;

const Title = styled.div`
  font-weight: ${p => p.active ? p.theme.fontWeight.bolder : p.theme.fontWeight.normal};
  color: ${p => p.active ? p.theme.colors.theme : p.theme.colors.text};
  font-size: 23px;
  text-transform: uppercase;
  margin-left: 20px;
  line-height: 50px;
  height: 50px;
`;

const InfoBlock = styled(Column)`
  border-right: 2px solid rgba(0, 149, 196, 0.24);
  padding-right: 8px !important;
  height: 100%;
  font-size: 19px;
`;

const OrderInfoLine = styled.span`
  font-weight: ${p => p.theme.fontWeight.bold};
  color: ${p => p.theme.colors.text};
  line-height: 50px;
  width: 100%;
  ${withEllipsis};
`;

export const StateText = styled.div`
  color: ${p => p.theme.colors.alerts[p.severity]};
  font-weight: ${p => p.theme.fontWeight.bolder};
  font-size: 12px;
  text-transform: uppercase;
  line-height: 50px;
  > span {
    font-size: 12px;
  }
`;

const CollapseMessage = styled.div`
  text-decoration: underline;
  font-size: 12px;
  line-height: 50px;
  color: #64747c;
  cursor: pointer;
  margin-right: 15px;
`;

const StyledIcon = styled(UpOutlined)`
  margin-left: 3px;
`;
const ActionButtons = styled(Span)`
  line-height: 50px;
  margin-left: 20px;
  height: 50px;
`;

const StyledSpan = styled(Span)`
  margin-left: 5px;
  margin-right: 20px;
  & > div {
    line-height: 50px;
  }
`;

const OrderInfoSpan = styled.div`
  font-weight: ${p => p.theme.fontWeight.bold};
  color: ${p => p.theme.colors.text};
  font-size: 19px;
  line-height: 50px;
`;

const BorderedContainer = styled.span`
  height: 20px;
  border-left: 1px solid #313b40;
  padding-left: 20px;
  margin-left: 20px;
  margin-right: 20px;
  ${media.desktop`
    border: none !important;
    margin-left: 15px;
  `}
`;

const StyledFlex = styled(Flex)`
  ${media.desktop`
    flex-flow: row wrap !important;
  `}
`;

const StyledStatusFlex = styled(StyledFlex)`
  width: fit-content;
  margin: 0 auto;
`;

const StatusBarMin = ({
  id,
  planneddate,
  progressinfo,
  hasmultipleorders,
  orderstates,
  activestatus,
  intl,
  maximize,
  getsavebutton,
  getdirtyindicator,
}) => {
  if (!orderstates || !activestatus || !orderstates.length) {
    return (
      <Bar type='flex' align='middle' gutter={16}>
        <InfoBlock sm={hasmultipleorders ? 21 : 2} xs={24}>
          <Spinner title={intl.formatMessage({ id: 'common.LOADING_STATUS' })} />
        </InfoBlock>
        {hasmultipleorders && <Column sm={3}>{progressinfo}</Column>}
      </Bar>
    );
  }

  return (
    <Bar type='flex' align='middle' gutter={16}>
      <InfoBlock sm={hasmultipleorders ? 12 : 14} xs={24}>
        <StyledFlex jcsb ffrn>
          <CollapseMessage onClick={maximize}>
            <FormattedMessage id='order.MAXIMIZE' />
            <StyledIcon type='up' />
          </CollapseMessage>
          <OrderInfoLine data-test-id='statusbar-sapid'>
            {id && <FormattedMessage id='statusBar.SAP_ID' values={{ number: id }} />}
          </OrderInfoLine>
          {planneddate &&
            <OrderInfoSpan data-test-id='statusbar-planned-date'>
              <BorderedContainer>
                <FormattedMessage id='order.plannedDate' /> {': '}
                <FormattedDate value={planneddate} />
              </BorderedContainer>
            </OrderInfoSpan>
          }
        </StyledFlex>
      </InfoBlock>
      <InfoBlock sm={hasmultipleorders ? 9 : 10} xs={24}>
        <StyledStatusFlex jcfs ffrn>
          <Title data-test-id='order-status-min' active={true}>
            <FormattedMessage id={`order.status.${activestatus.name}`} />
          </Title>
          <ActionButtons data-test-id='statusbar-action-buttons'>{getsavebutton}</ActionButtons>
          <StyledSpan>{getdirtyindicator}</StyledSpan>
        </StyledStatusFlex>
      </InfoBlock>
      {hasmultipleorders && <Column sm={3}>{progressinfo}</Column>}
    </Bar>
  );
};

StatusBarMin.propTypes = {
  id: PropTypes.string,
  planneddate: PropTypes.string,
  activestatus: PropTypes.object,
  orderstates: PropTypes.array,
  progressinfo: PropTypes.object,
  hasmultipleorders: PropTypes.bool,
  intl: PropTypes.object,
  maximize: PropTypes.func,
  getsavebutton: PropTypes.object,
  getdirtyindicator: PropTypes.object,
};

export default injectIntl(StatusBarMin);
